import { useEffect, useState } from 'react';
import { Alert, App, Form, Input, InputNumber, Modal } from 'antd';

import { unlockItem } from '../../services/records';

/**
 * A manager reopening a locked record.
 *
 * Three things the modal says out loud, because each is the point of the
 * feature: the unlock is time-boxed, it closes behind the next save, and the
 * reason is kept. An unlock nobody can see afterwards would make the lock
 * decorative.
 */
export default function ItemUnlockModal({ open, item, onClose, onUnlocked }) {

	const { message } = App.useApp();
	const [ reason, setReason ] = useState('');
	const [ minutes, setMinutes ] = useState(10);
	const [ saving, setSaving ] = useState(false);

	useEffect(() => {
		if (!open) return;
		setReason('');
		setMinutes(10);
	}, [ open, item?._id ]);

	const submit = async () => {
		if (!reason.trim()) {
			message.error('Unlocking a record needs a reason.');
			return;
		}
		setSaving(true);
		try {
			const result = await unlockItem(item._id, { reason: reason.trim(), minutes });
			message.success('Unlocked. The next save closes it again.');
			onUnlocked?.(result);
			onClose?.();
		}
		catch (err) {
			message.error(err?.response?.data?.message || 'Could not unlock this record.');
		}
		finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			onOk={submit}
			okText='Unlock'
			confirmLoading={saving}
			title='Unlock this record'
			destroyOnClose
		>
			<Alert
				type='warning'
				showIcon
				style={{ marginBottom: 12 }}
				message='This is kept'
				description='Your reason and your name stay with the record. The unlock closes again as soon as the record is saved.'
			/>
			<Form layout='vertical'>
				<Form.Item label='Why are you reopening it' required>
					<Input.TextArea
						rows={3}
						value={reason}
						autoFocus
						aria-label='Why are you reopening it'
						placeholder='Wrong drug recorded; fixing the entry.'
						onChange={event => setReason(event.target.value)}
					/>
				</Form.Item>
				<Form.Item label='For how long'>
					<InputNumber
						min={1}
						max={120}
						value={minutes}
						addonAfter='minutes'
						aria-label='Unlock duration in minutes'
						onChange={value => setMinutes(value || 10)}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
}
